import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { EditorPage } from "@/components/report-forms/editor/editor-page";
import { PageSkeleton } from "@/components/ui";
import { usePermissions } from "@/hooks/use-permissions";

interface ReportFormEditorSearch {
  /** The accepted submission the form is previewed with; absent while placing. */
  preview?: string;
}

export const Route = createFileRoute("/_authenticated/org/settings/report-forms/$id/edit")({
  component: ReportFormEditorRoute,
  validateSearch: (search: Record<string, unknown>): ReportFormEditorSearch => ({
    preview:
      typeof search.preview === "string" && search.preview !== "" ? search.preview : undefined,
  }),
});

/**
 * One report form's placement editor, a full-height surface under Org
 * Settings; `?preview=` puts it in Preview with that test.
 */
function ReportFormEditorRoute() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { preview } = Route.useSearch();
  const { can, isLoading } = usePermissions();

  // `usePermissions()` reads deny-all until its envelope resolves; deciding
  // earlier would bounce every reload of this URL to the home route.
  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (!can("org_settings", "read")) {
    void navigate({ to: "/" });
    return null;
  }

  // The shell's `main` reserves `pb-24` under every page for the floating
  // quick-actions launcher. A full-height editor has no trailing control, so
  // it grows by exactly that reserve and pulls it back with the negative
  // margin (the letter editors' layout does the same).
  return (
    <div className="-mb-24 h-[calc(100%+6rem)] print:mb-0 print:h-full">
      <EditorPage reportFormId={id} previewSubmissionId={preview ?? null} />
    </div>
  );
}
